import { useEffect, useRef, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";

interface FeatureVideoProps {
  mp4Src: string;
  webmSrc: string;
  poster?: string;
  alt: string;
  isActive: boolean;
  className?: string;
}

export const FeatureVideo = ({
  mp4Src,
  webmSrc,
  poster,
  alt,
  isActive,
  className = "",
}: FeatureVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Handle video playback based on active state
  useEffect(() => {
    const video = videoRef.current;
    if (!video || hasError) return;

    if (isActive && hasLoaded) {
      video.play().catch(() => {
        // Autoplay failed, this is okay
      });
    } else {
      video.pause();
    }
  }, [isActive, hasLoaded, hasError]);

  // Set up Intersection Observer for performance
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && isActive) {
            video.pause();
          } else if (entry.isIntersecting && isActive && hasLoaded) {
            video.play().catch(() => {});
          }
        });
      },
      { threshold: 0.5 }
    );

    observerRef.current.observe(video);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isActive, hasLoaded]);

  // Lazy load video sources only when active
  useEffect(() => {
    if (isActive && !hasLoaded) {
      setHasLoaded(true);
    }
  }, [isActive, hasLoaded]);

  const handleLoadedData = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  // Check for reduced motion preference
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (hasError) {
    return (
      <div
        className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-muted/50 ${className}`}
      >
        <div className="text-center p-8">
          <p className="text-muted-foreground">Video unavailable</p>
          <p className="text-sm text-muted-foreground/60 mt-2">
            Unable to load demo video
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`absolute inset-0 ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
          <Skeleton className="w-full h-full" />
        </div>
      )}
      
      <video
        ref={videoRef}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          isLoading ? "opacity-0" : "opacity-100"
        }`}
        muted
        loop
        playsInline
        preload={isActive ? "auto" : "metadata"}
        poster={poster}
        onLoadedData={handleLoadedData}
        onError={handleError}
        aria-label={alt}
      >
        {hasLoaded && !prefersReducedMotion && (
          <>
            <source src={webmSrc} type="video/webm" />
            <source src={mp4Src} type="video/mp4" />
          </>
        )}
        Your browser does not support the video tag.
      </video>
    </div>
  );
};
